/**
 * The DynamicLoad class provides dynamic loading functionality for different grid types (product, collection, article).
 * This class contains common properties and methods to be inherited by subclasses.
 */
class DynamicLoad extends HTMLElement {
  constructor() {
    super();

    // Selects DOM elements for different grid types
    this.itemGrid =
      document.querySelector("#product-grid") ||
      document.querySelector("#collection-grid") ||
      document.querySelector("#article-grid");

    // Determines the grid type
    if (this.itemGrid) {
      if (this.itemGrid.id === "collection-grid") {
        this.gridType = "collection";
      } else if (this.itemGrid.id === "product-grid") {
        this.gridType = "product";
      } else if (this.itemGrid.id === "article-grid") {
        this.gridType = "article";
      } else {
        this.gridType = "unknown";
      }
    } else {
      console.error("Grid element not found.");
      return;
    }

    this.setProperties();
  }

  /**
   * A placeholder method for subclasses to set additional properties.
   */
  setProperties() {
    // Additional properties can be set here if needed
  }

  getCardSelector() {
    const selectors = {
      product: ".product-card",
      collection: ".card-collection",
      article: ".card-article",
    };
    return selectors[this.gridType] || null;
  }

  getCards() {
    const cardSelector = this.getCardSelector();
    return cardSelector ? Array.from(this.itemGrid.querySelectorAll(cardSelector)) : [];
  }

  pageFromLink(link) {
    try {
      return parseInt(new URL(link, window.location.origin).searchParams.get("page"), 10) || 1;
    } catch (_) {
      return 1;
    }
  }

  // Cada card guarda su página para saber cuál se está viendo y volver a ella al recargar
  markCardsPage(cards, page) {
    cards.forEach((card) => {
      if (!card.dataset.page) card.dataset.page = String(page);
    });
  }

  /**
   * Appends newly loaded content to the existing grid.
   * @param {string} newContent - The HTML content to append.
   * @param {boolean} prepend - Determines whether to prepend or append the content.
   */
  appendContent(newContent, prepend = false) {
    if (prepend) {
      this.itemGrid.insertAdjacentHTML("afterbegin", newContent);
    } else {
      this.itemGrid.insertAdjacentHTML("beforeend", newContent);
    }
  }

  // Primera imagen de cada card nueva en eager para que empiece a descargarse al insertarla
  preloadImages(cards) {
    cards.forEach((card) => {
      const image = card.querySelector('img[loading="lazy"]');
      if (image) image.loading = "eager";
    });
  }

  /**
   * Updates the page URL.
   * @param {number} pageNumber - The new page number.
   */
  updateUrl(pageNumber) {
    const currentUrl = new URL(window.location.href);
    const currentUrlParams = new URLSearchParams(currentUrl.search);

    if (pageNumber === 1) {
      currentUrlParams.delete("page");
    } else {
      currentUrlParams.set("page", pageNumber);
    }

    currentUrl.search = currentUrlParams.toString();
    history.replaceState({}, "", currentUrl.toString());
  }

  /**
   * Updates the product/collection/article count.
   */
  updateItemCount() {
    let selector;
    switch (this.gridType) {
      case "product":
        selector = ".product-card";
        break;
      case "collection":
        selector = ".card-collection";
        break;
      case "article":
        selector = ".card-article";
        break;
      default:
        selector = ".card-item";
    }

    // Counts items within the itemGrid
    const itemCount = this.itemGrid.querySelectorAll(selector).length;

    if (this.gridType !== 'product') return;

    const itemCountOfAll = document.querySelector(
      `.collection-facets__product-count .product-count-of-all`
    );

    if (!itemCountOfAll) return;

    itemCountOfAll.innerText = itemCount;
  }
}

// DynamicLoadMore Custom Element
if (!customElements.get("dynamic-load-more")) {
  class DynamicLoadMore extends DynamicLoad {
    constructor() {
      super();

      this.btnLoadMore = this.querySelector(".js-btn-load-more");
      this.observer = null;
      this.isInfinityScrollActive = false;
      this.pagePrefetches = new Map();

      this.setProperties();
      this.toggleItemsVisibility();

      // Updates properties and visibility when a Shopify section is loaded
      window.addEventListener("shopify:section:load", () => {
        this.setProperties();
        this.toggleItemsVisibility();
      });

      this.updateItemCount();

      this.btnLoadMore.addEventListener("click", (e) => {
        e.preventDefault();
        this.loadMoreItems();
      });

      // Check and initialize Infinity Scroll
      this.currentPage = parseInt(this.dataset.currentPage, 10) || 1;
      this.markCardsPage(this.getCards(), this.currentPage);
      this.hasScrolled = false;
      this.checkAndSetupInfinityScroll();
      if (this.isInfinityScrollActive) {
        // Shopify sirve ?page=N solo con esa página, así que la posición la restaura el theme y no el navegador
        if ("scrollRestoration" in history) history.scrollRestoration = "manual";
        this.restoreScrollPosition();
        window.addEventListener("scroll", () => this.onScroll(), { passive: true });
        window.addEventListener("pagehide", () => this.saveScrollPosition());
      }
      this.updatePendingPagesState();
      this.schedulePrefetch();
    }

    onScroll() {
      if (!this.hasScrolled) {
        this.hasScrolled = true;
        this.updatePendingPagesState();
        // Si el disparador ya era visible antes del primer scroll el observer no volvería a avisar
        this.setupInfinityScroll();
      }
      if (this.scrollTicking) return;
      this.scrollTicking = true;
      requestAnimationFrame(() => {
        this.scrollTicking = false;
        this.syncUrlWithVisiblePage();
      });
    }

    getVisiblePageAnchor() {
      const cards = this.getCards();
      if (!cards.length) return null;

      // Búsqueda binaria de la primera card que asoma por la parte alta de la pantalla
      const threshold = window.innerHeight * 0.25;
      let low = 0;
      let high = cards.length - 1;
      while (low < high) {
        const middle = Math.floor((low + high) / 2);
        if (cards[middle].getBoundingClientRect().bottom > threshold) {
          high = middle;
        } else {
          low = middle + 1;
        }
      }

      const page = parseInt(cards[low].dataset.page, 10) || this.currentPage;
      const firstCard = cards.find((card) => card.dataset.page === String(page)) || cards[low];
      return { page, firstCard };
    }

    syncUrlWithVisiblePage() {
      const anchor = this.getVisiblePageAnchor();
      if (!anchor) return;

      const urlPage = parseInt(new URL(window.location.href).searchParams.get("page"), 10) || 1;
      if (anchor.page !== urlPage) this.updateUrl(anchor.page);
    }

    getScrollStorageKey() {
      return `plp-scroll:${window.location.pathname}`;
    }

    saveScrollPosition() {
      const anchor = this.getVisiblePageAnchor();
      if (!anchor) return;

      this.syncUrlWithVisiblePage();
      const firstCardTop = anchor.firstCard.getBoundingClientRect().top + window.scrollY;
      try {
        sessionStorage.setItem(this.getScrollStorageKey(), JSON.stringify({
          search: window.location.search,
          page: anchor.page,
          offset: Math.round(window.scrollY - firstCardTop),
        }));
      } catch (_) {}
    }

    restoreScrollPosition() {
      let saved = null;
      try {
        saved = JSON.parse(sessionStorage.getItem(this.getScrollStorageKey()) || "null");
        sessionStorage.removeItem(this.getScrollStorageKey());
      } catch (_) {}
      const navigationType = performance.getEntriesByType?.("navigation")[0]?.type;
      // Entrando desde un enlace nuevo la colección debe empezar arriba
      if (navigationType !== "reload" && navigationType !== "back_forward") return;
      if (!saved || saved.search !== window.location.search) return;

      const firstCard = this.getCards().find((card) => card.dataset.page === String(saved.page));
      if (!firstCard) return;

      requestAnimationFrame(() => {
        const top = firstCard.getBoundingClientRect().top + window.scrollY + saved.offset;
        window.scrollTo(0, Math.max(0, top));
      });
    }

    // Tras el primer scroll se oculta todo lo posterior al listado hasta cargar la \u00faltima p\u00e1gina (ver base.css)
    updatePendingPagesState() {
      const hasPendingPages =
        this.isInfinityScrollActive && this.hasScrolled && Boolean(this.btnLoadMore?.dataset.href);
      document.documentElement.classList.toggle("has-pending-pages", hasPendingPages);
    }

    requestPageText(link) {
      const pageUrl = new URL(link, window.location.origin);
      if (this.itemGrid.dataset.id) {
        pageUrl.searchParams.set("section_id", this.itemGrid.dataset.id);
      }
      return fetch(pageUrl.toString()).then((response) => response.text());
    }

    fetchPageText(link) {
      const cached = this.pagePrefetches.get(link);
      if (!cached) return this.requestPageText(link);

      this.pagePrefetches.delete(link);
      return cached.catch(() => this.requestPageText(link));
    }

    // Pide la siguiente página en segundo plano para que al hacer scroll solo falte insertarla
    prefetchNextPage() {
      const link = this.btnLoadMore?.dataset.href;
      if (!link || this.pagePrefetches.has(link)) return;

      const request = this.requestPageText(link);
      request.catch(() => this.pagePrefetches.delete(link));
      this.pagePrefetches.set(link, request);
    }

    schedulePrefetch() {
      if (!this.isInfinityScrollActive) return;

      if ("requestIdleCallback" in window) {
        requestIdleCallback(() => this.prefetchNextPage(), { timeout: 2000 });
      } else {
        setTimeout(() => this.prefetchNextPage(), 500);
      }
    }

    /**
     * Checks if infinity scroll should be set up and initializes it.
     */
    checkAndSetupInfinityScroll() {
      if (this.itemGrid) {
        const containerId =
          this.gridType === 'product' ? 'ProductGridContainer' :
          this.gridType === 'collection' ? 'CollectionGridContainer' :
          this.gridType === 'article' ? 'ArticleGridContainer' :
          null;

        if (containerId) {
          const parentContainer = this.itemGrid.closest(`#${containerId}`);
          if (parentContainer && parentContainer.classList.contains('infinity-scroll-active')) {
            this.isInfinityScrollActive = true;
            this.setupInfinityScroll();
          } else {
            this.isInfinityScrollActive = false;
          }
        } else {
          this.isInfinityScrollActive = false;
        }
      } else {
        this.isInfinityScrollActive = false;
      }
    }

    /**
     * Sets up the Intersection Observer for infinite scrolling.
     * The observer will trigger a click on the "Load More" button when it becomes visible.
     */
    setupInfinityScroll(startIndex = 0) {
      if (!this.btnLoadMore || !this.btnLoadMore.dataset.href) {
        return;
      }

      if (this.observer) {
        this.observer.disconnect();
      }

      let target = this.btnLoadMore;
      let rootMargin = "0px 0px 1000px 0px";

      // La siguiente página se pide cuando faltan 2 filas para el final de lo ya cargado
      const cardSelector = this.getCardSelector();
      if (cardSelector) {
        const cards = this.itemGrid.querySelectorAll(cardSelector);
        const gridStyles = window.getComputedStyle(this.itemGrid);
        const columns = gridStyles.display === "grid"
          ? gridStyles.gridTemplateColumns.split(" ").length
          : 1;
        const triggerCard = cards[Math.min(Math.max(startIndex, cards.length - columns * 2), cards.length - 1)];

        if (triggerCard) {
          target = triggerCard;
          rootMargin = "0px";
        }
      }

      this.observer = new IntersectionObserver((entries) => {
        // Con scroll rápido el disparador queda por encima de la pantalla, así que también cuenta haberlo superado
        const shouldLoad = entries.some((entry) =>
          entry.isIntersecting ||
          (entry.rootBounds && entry.boundingClientRect.bottom <= entry.rootBounds.top)
        );
        if (!shouldLoad) return;
        // Sin scroll del usuario no se carga nada aunque el disparador ya esté en pantalla
        if (!this.hasScrolled) return;
        if (this.btnLoadMore.hasAttribute("disabled")) return;

        this.observer.disconnect();
        this.btnLoadMore.click();
      }, {
        root: null,
        rootMargin,
        threshold: 0
      });

      this.observer.observe(target);
      if (target !== this.btnLoadMore) {
        this.observer.observe(this.btnLoadMore);
      }
    }

    /**
     * Controls the visibility of the "Load More" button.
     */
    toggleItemsVisibility() {
      const nextPageLink = this.btnLoadMore.dataset.href;
      if (!nextPageLink) {
        this.btnLoadMore.style.display = "none";
      } else {
        this.btnLoadMore.style.display = "";
      }
    }

    /**
     * Loads more products/collections/articles.
     */
    async loadMoreItems() {
      this.btnLoadMore.setAttribute("disabled", "true");
      const nextPageLink = this.btnLoadMore.dataset.href;

      if (!nextPageLink) {
        this.btnLoadMore.removeAttribute("disabled");
        return;
      }

      let loadMoreSucceeded = false;

      try {
        const nextPageText = await this.fetchPageText(nextPageLink);

        const nextPageHTML = new DOMParser().parseFromString(
          nextPageText,
          "text/html"
        );

        // Find the relevant grid element from the new page
        let newGridContainer;
        if (this.gridType === "product") {
          newGridContainer = nextPageHTML.querySelector("#ProductGridContainer");
        } else if (this.gridType === "collection") {
          newGridContainer = nextPageHTML.querySelector("#CollectionGridContainer");
        } else if (this.gridType === "article") {
          newGridContainer = nextPageHTML.querySelector("#ArticleGridContainer");
        }

        const newGrid = newGridContainer
          ? newGridContainer.querySelector(`#${this.gridType}-grid`)
          : null;

        const previousCardCount = this.itemGrid.querySelectorAll(this.getCardSelector() || "*").length;

        if (newGrid) {
          this.appendContent(newGrid.innerHTML);
          const cardSelector = this.getCardSelector();
          if (cardSelector) {
            const newCards = Array.from(this.itemGrid.querySelectorAll(cardSelector)).slice(previousCardCount);
            this.markCardsPage(newCards, this.pageFromLink(nextPageLink));
            this.preloadImages(newCards);
          }
        } else {
          console.warn(`New grid element (#${this.gridType}-grid) not found in fetched page.`);
        }

        // After appending content, re-query for the button to ensure it's the current one
        this.btnLoadMore = this.querySelector(".js-btn-load-more");
        if (!this.btnLoadMore) {
          console.error("Load More button disappeared after content append. Cannot continue.");
          return;
        }

        // Find the next page button and update its href
        const nextButtonOnNewPage = nextPageHTML.querySelector(
          ".js-btn-load-more"
        );
        if (nextButtonOnNewPage) {
          const nextPageLinkNew = nextButtonOnNewPage.dataset.href;
          this.btnLoadMore.dataset.href = nextPageLinkNew;
        } else {
          this.btnLoadMore.dataset.href = "";
        }

        // La URL sigue a la página que se está viendo, no a la última cargada
        this.syncUrlWithVisiblePage();

        this.updateItemCount();
        this.toggleItemsVisibility();
        this.updatePendingPagesState();

        loadMoreSucceeded = true;
        requestAnimationFrame(() => {
          if (!this.btnLoadMore) return;

          this.btnLoadMore.removeAttribute("disabled");

          // El observer se rearma con el botón ya libre para no perder el disparo
          if (this.isInfinityScrollActive) {
            this.setupInfinityScroll(previousCardCount);
            this.schedulePrefetch();
          }
        });

      } catch (error) {
        console.error("Load more error:", error);
        // Si falla la carga no se puede dejar el footer inaccesible
        document.documentElement.classList.remove("has-pending-pages");
        // Show error message to the user (a better UI should be used instead of alert)
        const errorMessage = document.createElement('div');
        errorMessage.className = 'error-message p-4 bg-red-100 border border-red-400 text-red-700 rounded-md mt-4';
        errorMessage.textContent = `An error occurred: ${error.message}`;
        this.btnLoadMore.after(errorMessage);
        setTimeout(() => errorMessage.remove(), 5000);
      } finally {
        if (!loadMoreSucceeded) {
          this.btnLoadMore.removeAttribute("disabled");
        }
      }
    }
  }

  customElements.define("dynamic-load-more", DynamicLoadMore);
}

// DynamicLoadPrevious Custom Element
if (!customElements.get("dynamic-load-previous")) {
  class DynamicLoadPrevious extends DynamicLoad {
    constructor() {
      super();

      this.btnLoadPrevious = this.querySelector(".js-btn-load-previous");
      this.setProperties();
      this.checkPageParameter();

      // Updates properties and page parameter when a Shopify section is loaded
      window.addEventListener("shopify:section:load", () => {
        this.setProperties();
        this.checkPageParameter();
      });

      this.updateItemCount();

      this.btnLoadPrevious.addEventListener("click", (e) => {
        e.preventDefault();
        this.loadPreviousItems();
      });

      // Con scroll infinito la página anterior se carga sola al subir, sin pulsar el botón
      this.isAutoLoad = Boolean(this.itemGrid.closest(".infinity-scroll-active"));
      if (this.isAutoLoad) {
        this.lastScrollY = window.scrollY;
        window.addEventListener("scroll", () => this.onScroll(), { passive: true });
        // Rueda, gesto o teclado indican scroll del usuario; el de restaurar la posición no cuenta
        const markUserScroll = () => this.classList.add("is-user-scrolled");
        ["wheel", "touchmove", "keydown"].forEach((eventName) => {
          window.addEventListener(eventName, markUserScroll, { passive: true, once: true });
        });
      }
    }

    onScroll() {
      if (this.scrollTicking) return;
      this.scrollTicking = true;
      requestAnimationFrame(() => {
        this.scrollTicking = false;
        const scrollY = window.scrollY;
        const isScrollingUp = scrollY < this.lastScrollY;
        this.lastScrollY = scrollY;

        if (!isScrollingUp || this.isLoading || !this.btnLoadPrevious?.dataset.href) return;
        if (this.getBoundingClientRect().bottom > -window.innerHeight) this.loadPreviousItems();
      });
    }

    /**
     * Checks the page parameter and sets the visibility of the "Load Previous" button.
     */
    checkPageParameter() {
      const currentUrl = new URL(window.location.href);
      const currentUrlParams = new URLSearchParams(currentUrl.search);
      const currentPage = parseInt(currentUrlParams.get("page"), 10) || 1;

      if (currentPage <= 1) {
        this.btnLoadPrevious.style.display = "none";
      } else {
        this.btnLoadPrevious.style.display = "";
      }
    }

    /**
     * Loads previous products/collections/articles.
     */
    async loadPreviousItems() {
      if (this.isLoading) return;
      this.isLoading = true;
      this.btnLoadPrevious.setAttribute("disabled", "true");
      const prevPageLink = this.btnLoadPrevious.dataset.href;

      if (!prevPageLink) {
        this.isLoading = false;
        this.btnLoadPrevious.removeAttribute("disabled");
        return;
      }

      try {
        const prevPageUrl = new URL(prevPageLink, window.location.origin);
        if (this.itemGrid.dataset.id) prevPageUrl.searchParams.set("section_id", this.itemGrid.dataset.id);
        const prevPageResponse = await fetch(prevPageUrl.toString());
        const prevPageText = await prevPageResponse.text();

        const prevPageHTML = new DOMParser().parseFromString(
          prevPageText,
          "text/html"
        );

        // Find the relevant grid element from the new page
        let newGridContainer;
        if (this.gridType === "product") {
          newGridContainer = prevPageHTML.querySelector("#ProductGridContainer");
        } else if (this.gridType === "collection") {
          newGridContainer = prevPageHTML.querySelector("#CollectionGridContainer");
        } else if (this.gridType === "article") {
          newGridContainer = prevPageHTML.querySelector("#ArticleGridContainer");
        }

        const newGrid = newGridContainer
          ? newGridContainer.querySelector(`#${this.gridType}-grid`)
          : null;

        if (newGrid) {
          const previousCardCount = this.getCards().length;
          // Se mide una card ya visible para devolverla al mismo sitio tras insertar contenido encima
          const referenceCard = this.getCards()[0];
          const referenceTop = referenceCard?.getBoundingClientRect().top;
          const rootStyle = document.documentElement.style;
          const previousOverflowAnchor = rootStyle.overflowAnchor;
          rootStyle.overflowAnchor = "none";

          this.appendContent(newGrid.innerHTML, true);
          const cards = this.getCards();
          this.markCardsPage(cards.slice(0, cards.length - previousCardCount), this.pageFromLink(prevPageLink));

          if (referenceCard) {
            window.scrollBy(0, referenceCard.getBoundingClientRect().top - referenceTop);
            this.lastScrollY = window.scrollY;
          }
          rootStyle.overflowAnchor = previousOverflowAnchor;
        } else {
          console.warn(`New grid element (#${this.gridType}-grid) not found in fetched page.`);
        }

        // After appending content, re-query for the button to ensure it's the current one
        this.btnLoadPrevious = this.querySelector(".js-btn-load-previous");
        if (!this.btnLoadPrevious) {
          console.error("Load Previous button disappeared after content prepend. Cannot continue.");
          return;
        }

        // Find the previous page button and update its href
        const prevButtonOnNewPage = prevPageHTML.querySelector(
          ".js-btn-load-previous"
        );
        if (prevButtonOnNewPage) {
          const prevPageLinkNew = prevButtonOnNewPage.dataset.href;
          this.btnLoadPrevious.dataset.href = prevPageLinkNew;
        } else {
          this.btnLoadPrevious.dataset.href = "";
        }

        if (this.isAutoLoad) {
          // La URL ya la sincroniza dynamic-load-more con la página visible
          if (!this.btnLoadPrevious.dataset.href) this.style.display = "none";
        } else {
          const currentUrl = new URL(window.location.href);
          const currentUrlParams = new URLSearchParams(currentUrl.search);
          const currentPage = parseInt(currentUrlParams.get("page"), 10) || 1;
          this.updateUrl(currentPage - 1);
          this.checkPageParameter();
        }

        this.updateItemCount();
      } catch (error) {
        console.error("Load previous error:", error);
        // Show error message to the user (a better UI should be used instead of alert)
        const errorMessage = document.createElement('div');
        errorMessage.className = 'error-message p-4 bg-red-100 border border-red-400 text-red-700 rounded-md mt-4';
        errorMessage.textContent = `An error occurred: ${error.message}`;
        this.btnLoadPrevious.after(errorMessage);
        setTimeout(() => errorMessage.remove(), 5000);
      } finally {
        this.isLoading = false;
        this.btnLoadPrevious.removeAttribute("disabled");
      }
    }
  }

  customElements.define("dynamic-load-previous", DynamicLoadPrevious);
}
